import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { captureManifestSnapshot, fetchManifestShop, fetchManifestSignatureUrls } from '@/utils/deliveryManifest';
import { printDriverManifest, printRunManifest } from '@/utils/printRunManifest';

// One synchronous admission lease covers both buttons. The owner key is the
// read/session/filter identity, not a mutable authorization token or user ID.
export default function useDeliveryManifestPrint({ ownerKey, canPrint, getToken, snapshot, isCurrent = () => true }) {
  const latest = useRef(null);
  latest.current = { ownerKey, canPrint, getToken, snapshot, isCurrent };
  const mounted = useRef(false);
  const pending = useRef(null);
  const documents = useRef(new Set());
  const [busy, setBusy] = useState(null);

  useLayoutEffect(() => {
    mounted.current = true;
    setBusy(null);
    const ownedDocuments = documents.current;
    return () => {
      mounted.current = false;
      ownedDocuments.forEach((operation) => operation.cancel());
      ownedDocuments.clear();
      pending.current = null;
    };
  }, [ownerKey, canPrint]);

  const start = useCallback(async (kind) => {
    const input = latest.current;
    if (!mounted.current || !input.canPrint || !input.isCurrent() || pending.current) return;
    let captured;
    let token;
    try {
      captured = captureManifestSnapshot(kind, input.snapshot);
      token = input.getToken();
      if (!token || !input.isCurrent()) return;
    } catch (error) {
      toast.error(error.message || 'Cannot prepare this snapshot');
      return;
    }
    // Must remain in the click stack: never open another window after await.
    const popup = window.open('', '_blank', 'width=900,height=1100');
    if (!popup) { toast.error('Pop-ups blocked — allow pop-ups to print'); return; }
    const controller = new AbortController();
    const operation = { ownerKey: input.ownerKey, published: false, cancelled: false, cancelAutoPrint: null };
    let closePoll;
    const current = () => mounted.current && !operation.cancelled && !popup.closed
      && latest.current.ownerKey === input.ownerKey && latest.current.canPrint
      && input.isCurrent() && latest.current.isCurrent();
    operation.cancel = () => {
      if (operation.cancelled) return;
      operation.cancelled = true;
      controller.abort();
      clearInterval(closePoll);
      operation.cancelAutoPrint?.();
      if (!operation.published && !popup.closed) popup.close();
      documents.current.delete(operation);
      if (pending.current === operation) {
        pending.current = null;
        if (mounted.current) setBusy(null);
      }
    };
    pending.current = operation;
    documents.current.add(operation);
    setBusy({ ownerKey: input.ownerKey, kind });
    try {
      popup.document.open();
      popup.document.write('<!doctype html><html><head><title>Preparing delivery snapshot</title></head><body>Preparing loaded-record snapshot…</body></html>');
      popup.document.close();
      closePoll = setInterval(() => { if (!current()) operation.cancel(); }, 250);
      const signatureUrls = await fetchManifestSignatureUrls(captured.orders, token, undefined, {
        signal: controller.signal, isCurrent: current,
      });
      if (!current()) { operation.cancel(); return; }
      const shop = await fetchManifestShop(undefined, { signal: controller.signal });
      if (!current()) { operation.cancel(); return; }
      const publish = kind === 'run' ? printRunManifest : printDriverManifest;
      const printed = publish({
        ...captured.payload, signatureUrls, shop, popup, isCurrent: current,
        onAutoPrintSettled: () => documents.current.delete(operation),
      });
      if (!printed) { operation.cancel(); return; }
      operation.published = true;
      operation.cancelAutoPrint = printed.cancelAutoPrint;
    } catch (error) {
      if (current() && error?.name !== 'AbortError') toast.error('Failed to prepare manifest');
      operation.cancel();
    } finally {
      clearInterval(closePoll);
      if (pending.current === operation) {
        pending.current = null;
        if (mounted.current) setBusy(null);
      }
    }
  }, []);

  return {
    printRun: useCallback(() => start('run'), [start]),
    printDriver: useCallback(() => start('driver'), [start]),
    printing: busy?.ownerKey === ownerKey && busy.kind === 'run',
    printingDriver: busy?.ownerKey === ownerKey && busy.kind === 'driver',
  };
}
